// SSE streaming client for YNX backend - robust error handling
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Approximate budget. Most models accept >=128K tokens but we keep wide
// margin for the response (~32K) and for hidden system/tool tokens.
// 1 token ≈ 4 chars on average. 80K tokens ≈ 320 000 chars.
const MAX_HISTORY_CHARS = 320_000;

const safeReadText = async (resp) => {
  try {
    return await resp.text();
  } catch (_) {
    return `HTTP ${resp.status} ${resp.statusText || ''}`.trim();
  }
};

/** Trim conversation history to stay under MAX_HISTORY_CHARS.
 *  Strategy: keep the most recent messages, drop the oldest first.
 *  Always preserves at least the last user/assistant exchange.
 */
const trimMessages = (msgs) => {
  if (!Array.isArray(msgs) || msgs.length === 0) return [];
  // Build from the END backwards, including messages until budget exceeded.
  const trimmed = [];
  let total = 0;
  for (let i = msgs.length - 1; i >= 0; i--) {
    const c = msgs[i].content || '';
    if (total + c.length > MAX_HISTORY_CHARS && trimmed.length >= 2) break;
    trimmed.unshift(msgs[i]);
    total += c.length;
  }
  return trimmed;
};

/**
 * Stream a chat completion from the backend.
 * Calls onDelta(chunk) for each token chunk, onDone() at end, onError(err).
 * Returns an abort handle: { abort() }.
 */
export const streamChat = async ({ sessionId, model, systemMessage, messages, userMessage, customApiKey, attachments, onDelta, onDone, onError, onRoute }) => {
  const controller = new AbortController();
  // Trim history before sending — prevents context-window blowups.
  const trimmedHistory = trimMessages(messages || []);
  const body = {
    session_id: sessionId,
    model,
    system_message: systemMessage,
    messages: trimmedHistory.map((m) => ({ role: m.role, content: m.content })),
    user_message: userMessage,
  };
  if (customApiKey && customApiKey.trim()) {
    body.custom_api_key = customApiKey.trim();
  }
  if (attachments && attachments.length > 0) {
    body.attachments = attachments.map((a) => ({
      name: a.name,
      mime_type: a.mime_type,
      data_base64: a.data_base64,
    }));
  }

  let done = false;
  const safeError = (err) => {
    if (done) return;
    done = true;
    onError?.(err);
  };
  const safeDone = () => {
    if (done) return;
    done = true;
    onDone?.();
  };

  (async () => {
    let resp;
    try {
      resp = await fetch(`${API_BASE}/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'text/event-stream' },
        body: JSON.stringify(body),
        signal: controller.signal,
        cache: 'no-store',
      });
    } catch (e) {
      if (e.name !== 'AbortError') safeError(new Error(`Connexion impossible : ${e.message}`));
      return;
    }

    if (!resp.ok) {
      const txt = await safeReadText(resp);
      safeError(new Error(`HTTP ${resp.status} — ${txt.slice(0, 200)}`));
      return;
    }

    if (!resp.body) {
      // Some proxies strip streaming; fall back to reading full body once
      const txt = await safeReadText(resp);
      // Try to parse SSE manually from the full text
      const lines = txt.split('\n');
      for (const line of lines) {
        if (!line.startsWith('data:')) continue;
        const data = line.slice(5).trim();
        if (!data) continue;
        try {
          const obj = JSON.parse(data);
          if (obj.type === 'delta') onDelta?.(obj.content);
          else if (obj.type === 'error') { safeError(new Error(obj.message)); return; }
        } catch (parseErr) { console.debug('YNX SSE: bad JSON frame', parseErr); }
      }
      safeDone();
      return;
    }

    const reader = resp.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';

    try {
      while (true) {
        const { value, done: streamDone } = await reader.read();
        if (streamDone) {
          safeDone();
          break;
        }
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';
        for (const part of parts) {
          const dataLines = part.split('\n').filter((l) => l.startsWith('data:'));
          for (const line of dataLines) {
            const data = line.slice(5).trim();
            if (!data) continue;
            try {
              const obj = JSON.parse(data);
              if (obj.type === 'delta') onDelta?.(obj.content);
              else if (obj.type === 'route') onRoute?.(obj);
              else if (obj.type === 'done') { safeDone(); return; }
              else if (obj.type === 'error') { safeError(new Error(obj.message)); return; }
            } catch (parseErr) { console.warn('YNX: SSE parse error', parseErr, data); }
          }
        }
      }
    } catch (e) {
      if (e.name !== 'AbortError') safeError(new Error(`Erreur de stream : ${e.message}`));
    }
  })();

  return { abort: () => { try { controller.abort(); } catch (err) { console.warn('YNX: controller abort failed', err); } } };
};
